import React, { useState, useEffect, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Container,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    FormHelperText,
    Paper,
    Box,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardActions,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar
} from '@mui/material';
import Header from './components/Header';
import generateCertificate from './utils/certificateGenerator';
import { logUserEvent } from './utils/logger';
import { useAuth } from './context/AuthContext';

const Question = memo(({ question, index, answers, handleAnswerChange, isSubmitted, passed, submittedAnswers, validationError }) => {
    const selectedAnswer = answers[question.id];
    const submittedAnswer = submittedAnswers[question.id];
    const correctAnswerText = question.answers.find(a => a.isCorrect)?.text;
    const isIncorrect = isSubmitted && !passed && submittedAnswer && submittedAnswer !== correctAnswerText;
    const isCorrect = isSubmitted && submittedAnswer === correctAnswerText;
    const hasError = validationError && !selectedAnswer;

    return (
        <FormControl
            component={Paper}
            elevation={3}
            key={question.id}
            error={hasError}
            sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: hasError ? '2px solid #d32f2f' : (isIncorrect ? '2px solid red' : (isCorrect ? '2px solid green' : 'none')),
                width: '100%',
                boxSizing: 'border-box'
            }}
        >
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                {index + 1}. {question.question}
            </FormLabel>
            <RadioGroup
                name={question.id.toString()}
                value={selectedAnswer || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            >
                {question.answers.map((answer, optionIndex) => (
                    <FormControlLabel
                        key={optionIndex}
                        value={answer.text}
                        control={<Radio />}
                        label={answer.text}
                        disabled={passed}
                    />
                ))}
            </RadioGroup>
            {hasError && (
                <FormHelperText>Please select an answer for this question</FormHelperText>
            )}
        </FormControl>
    );
});

const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
};

const Quiz = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const PASS_THRESHOLD = 85; // percent required to pass

    const [course, setCourse] = useState(null);
    const [answers, setAnswers] = useState({});
    const [score, setScore] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedAnswers, setSubmittedAnswers] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authenticityCode, setAuthenticityCode] = useState(null);
    const [showCongrats, setShowCongrats] = useState(false);
    const [showProfileWarning, setShowProfileWarning] = useState(false);
    const [celebrated, setCelebrated] = useState(false);
    const [certSnackbar, setCertSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [validationError, setValidationError] = useState(false);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [unansweredCount, setUnansweredCount] = useState(0);

    const [attemptInfo, setAttemptInfo] = useState(null);
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);

    useEffect(() => {
        if (authLoading) return; // wait for auth init
        if (!user) {
            navigate('/login');
            return;
        }

        const fetchCourse = async () => {
            try {
                const response = await axios.get(`/api/courses/${courseId}`);
                const courseData = response.data;
                if (courseData.questions) {
                    courseData.questions = courseData.questions.map(q => {
                        const transformedAnswers = q.multiple_choices.map((choice, index) => ({
                            text: choice,
                            isCorrect: index === q.correct_answer
                        }));
                        return { ...q, answers: shuffleArray(transformedAnswers) };
                    });
                }
                setCourse(courseData);
                logUserEvent('quiz_start', `Started quiz for course: ${courseData.title}`, { course_id: courseId });
            } catch (err) {
                setError('Failed to load quiz data. Please make sure the backend server is running and the course ID is correct.');
            } finally {
                setLoading(false);
            }
        };
        fetchCourse();
    }, [courseId, navigate, user, authLoading]);

    const handleAnswerChange = useCallback((questionId, answer) => {
        setAnswers(prevAnswers => ({ ...prevAnswers, [questionId]: answer }));
        // Clear validation error when user selects an answer
        if (validationError) {
            setValidationError(false);
        }
    }, [validationError]);

    const handleDownloadCertificate = async () => {
        // Validate that user has provided name and surname
        if (!user?.name || !user?.surname) {
            setShowProfileWarning(true);
            return;
        }

        const userName = user.name;
        const userSurname = user.surname;
        if (!course || !authenticityCode) {
            alert("Certificate data is not ready. Please ensure the course is completed.");
            return;
        }
        try {
            const result = await generateCertificate(
                course.title,
                (course.complexity || course.complexity_name || ''),
                userName,
                userSurname,
                course.title,
                authenticityCode
            );

            if (result.success) {
                setCertSnackbar({
                    open: true,
                    message: `🎉 Certificate created successfully! Check your downloads folder for ${result.fileName}`,
                    severity: 'success'
                });
                // Navigate back to dashboard after a brief delay
                setTimeout(() => navigate('/dashboard'), 2000);
            } else {
                setCertSnackbar({
                    open: true,
                    message: `❌ Error generating certificate: ${result.error}`,
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error("Failed to generate certificate:", error);
            setCertSnackbar({
                open: true,
                message: '❌ Failed to generate certificate. Please try again.',
                severity: 'error'
            });
        }
    };

    const handleProfileWarningClose = () => {
        setShowProfileWarning(false);
        navigate('/profile', {
            state: {
                fromCertificate: true,
                courseId: courseId
            }
        });
    };

    const handleCloseCongratsDialog = () => {
        setShowCongrats(false);
        navigate('/dashboard');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate that all questions have been answered
        const unansweredQuestions = course.questions.filter(q => !answers[q.id]);
        if (unansweredQuestions.length > 0) {
            setValidationError(true);
            setUnansweredCount(unansweredQuestions.length);
            setValidationDialogOpen(true);
            // Scroll to first unanswered question
            const firstUnansweredIndex = course.questions.findIndex(q => !answers[q.id]);
            if (firstUnansweredIndex !== -1) {
                const element = document.getElementsByName(course.questions[firstUnansweredIndex].id.toString())[0];
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            return;
        }

        // Clear validation error
        setValidationError(false);

        let newScore = 0;
        course.questions.forEach(question => {
            const correctAnswerText = question.answers.find(a => a.isCorrect)?.text;
            if (answers[question.id] === correctAnswerText) newScore++;
        });

        setScore(newScore);
        setIsSubmitted(true);
        setSubmittedAnswers(answers);

        const percentage = Math.round((newScore / course.questions.length) * 100);
        const userId = user?.id;
        const courseTitle = course.title;

        if (userId && courseTitle) {
            await logQuizResult(userId, courseTitle, percentage);
            // Save result regardless of pass/fail to track attempts
            await saveCourseResult(userId, courseId, percentage);
        }
    };

    const saveCourseResult = async (userId, courseId, score) => {
        try {
            const resultData = { user_id: userId, course_id: courseId, score: score };
            const response = await axios.post('/api/course_results/', resultData);
            console.log('Course result saved successfully.');

            // If passed, set authenticity code
            if (score >= PASS_THRESHOLD) {
                setAuthenticityCode(response.data.authenticity_code);
            }
            // If failed, capture attempt info
            else if (response.data.failed_attempts_today !== undefined) {
                setAttemptInfo({
                    failed_attempts_today: response.data.failed_attempts_today,
                    max_attempts: response.data.max_attempts,
                    attempts_remaining: response.data.attempts_remaining
                });
                setWarningDialogOpen(true);
            }

            return response.data.authenticity_code;
        } catch (error) {
            console.error('Error saving course result:', error);

            // Handle attempt limit reached (429)
            if (error.response && error.response.status === 429) {
                const data = error.response.data.detail;
                setAttemptInfo({
                    limitReached: true,
                    failed_attempts_today: data.failed_attempts_today,
                    max_attempts: data.max_attempts,
                    attempts_remaining: 0,
                    message: data.message
                });
                setWarningDialogOpen(true);
            }

            // Don't re-throw if it's just the limit reached, as we handled it in UI
            if (error.response?.status !== 429) {
                throw error;
            }
        }
    };

    const logQuizResult = async (userId, courseTitle, percentage) => {
        try {
            const logData = { user_id: userId, event: percentage >= PASS_THRESHOLD ? 'course_complete' : 'course_failed', description: courseTitle };
            await axios.post('/api/user_logs/', logData);
            console.log('Quiz attempt logged successfully.');
        } catch (error) {
            console.error('Failed to log quiz result:', error);
            throw error;
        }
    };

    // Gamification: show score only after submission (no real-time hints)
    const totalQuestions = course?.questions?.length || 0;
    const submittedPercent = totalQuestions ? Math.round((score / totalQuestions) * 100) : 0;
    useEffect(() => {
        if (isSubmitted && !celebrated && submittedPercent >= PASS_THRESHOLD && totalQuestions > 0) {
            setShowCongrats(true);
            setCelebrated(true);
        }
    }, [isSubmitted, submittedPercent, totalQuestions, celebrated]);

    if (loading || authLoading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
            }}>
                <Header />
                <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
            }}>
                <Header />
                <Container>
                    <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
                    <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>Return to Dashboard</Button>
                </Container>
            </Box>
        );
    }

    if (!course) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
            }}>
                <Header />
                <Container>
                    <Alert severity="warning" sx={{ mt: 4 }}>Course not found.</Alert>
                </Container>
            </Box>
        );
    }

    const percentage = Math.round((score / course.questions.length) * 100);
    const passed = percentage >= PASS_THRESHOLD;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
        }}>
            <Header />
            <Container component={Paper} sx={{ p: 4, mt: 4, borderRadius: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h4" component="h1" gutterBottom>{course.title}</Typography>
                </Box>
                <main>
                    <form onSubmit={handleSubmit}>
                        {course.questions.map((question, index) => (
                            <Question
                                key={question.id}
                                question={question}
                                index={index}
                                answers={answers}
                                handleAnswerChange={handleAnswerChange}
                                isSubmitted={isSubmitted}
                                passed={passed}
                                submittedAnswers={submittedAnswers}
                                validationError={validationError}
                            />
                        ))}
                        {!passed && !attemptInfo?.limitReached && (
                            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                                Submit Answers
                            </Button>
                        )}
                    </form>

                    {isSubmitted && (
                        <Card sx={{ mt: 4 }}>
                            <CardContent>
                                {passed ? (
                                    <>
                                        <Typography variant="h5" color="success.main">Congratulations!</Typography>
                                        <Typography>You passed with a score of {percentage}%.</Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="h5" color="error.main">Keep trying!</Typography>
                                        <Typography>Your score is {percentage}%. A score of {PASS_THRESHOLD}% is required to pass.</Typography>
                                        <Typography color="text.secondary" sx={{ mb: 2 }}>Your incorrect answers are highlighted. Review them and submit again.</Typography>

                                        {/* Attempt info is now shown in a dialog */}
                                    </>
                                )}
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'center' }}>
                                {passed && (
                                    <Button variant="contained" onClick={handleDownloadCertificate}>
                                        Download Certificate
                                    </Button>
                                )}
                                <Button variant="outlined" onClick={() => navigate('/dashboard')}>
                                    Back to Dashboard
                                </Button>
                            </CardActions>
                        </Card>
                    )}
                </main>
            </Container>

            {/* Floating gamification footer */}
            {isSubmitted && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'background.paper',
                        borderTop: '1px solid #e0e0e0',
                        p: 2,
                        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                        zIndex: (theme) => theme.zIndex.appBar - 1,
                    }}
                >
                    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Score: {submittedPercent}% ({score}/{totalQuestions} correct)
                        </Typography>
                        <LinearProgress variant="determinate" value={submittedPercent} sx={{ height: 10, borderRadius: 5 }} />
                    </Box>
                </Box>
            )}

            {/* Celebration dialog */}
            <Dialog open={showCongrats} onClose={handleCloseCongratsDialog}>
                <DialogTitle>🎉 Congratulations!</DialogTitle>
                <DialogContent>
                    <Typography>
                        You passed the course and scored {submittedPercent}%.
                    </Typography>
                    <Typography>
                        Now download your PDF certificate.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    {authenticityCode ? (
                        <Button variant="contained" onClick={handleDownloadCertificate}>Download Certificate</Button>
                    ) : null}
                    <Button onClick={handleCloseCongratsDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Profile warning dialog */}
            <Dialog open={showProfileWarning} onClose={() => setShowProfileWarning(false)}>
                <DialogTitle>⚠️ Profile Incomplete</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Please complete your profile by providing your name and surname before downloading your certificate.
                    </Typography>
                    <Typography>
                        You will be redirected to your profile page.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleProfileWarningClose}>OK - Go to Profile</Button>
                </DialogActions>
            </Dialog>

            {/* Validation Error Dialog */}
            <Dialog open={validationDialogOpen} onClose={() => setValidationDialogOpen(false)}>
                <DialogTitle>⚠️ Incomplete Quiz</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Please answer all questions before submitting.
                    </Typography>
                    <Typography>
                        You have <strong>{unansweredCount}</strong> unanswered question{unansweredCount !== 1 ? 's' : ''}.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationDialogOpen(false)} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Attempt Warning Dialog */}
            <Dialog open={warningDialogOpen} onClose={() => setWarningDialogOpen(false)}>
                <DialogTitle>
                    {attemptInfo?.limitReached ? "⚠️ Daily Limit Reached" : "⚠️ Attempt Used"}
                </DialogTitle>
                <DialogContent>
                    {attemptInfo?.limitReached ? (
                        <Typography>
                            You've reached the daily limit of {attemptInfo.max_attempts} failed attempts.
                            Please study the textbook, podcast and video again.
                            You may retry this test again tomorrow, it will again start with a 'clean slate'.
                        </Typography>
                    ) : (
                        <Box>
                            <Typography variant="h6" color="error.main" sx={{ mb: 1 }}>
                                Your score is {percentage}%. A score of {PASS_THRESHOLD}% is required to pass.
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                Your incorrect answers are highlighted. Review them and submit again.
                            </Typography>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                                Attempt {attemptInfo?.failed_attempts_today} of {attemptInfo?.max_attempts} examination attempts per day used.
                            </Typography>
                            <Typography>
                                {attemptInfo?.attempts_remaining === 1
                                    ? "You have 1 attempt remaining today. Study the material carefully before trying again! If you fail this final attempt, you'll be blocked until tomorrow."
                                    : attemptInfo?.attempts_remaining === 2
                                        ? "You have 2 attempts remaining today."
                                        : `${attemptInfo?.attempts_remaining} attempts remaining today.`}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWarningDialogOpen(false)} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Certificate download snackbar */}
            <Snackbar
                open={certSnackbar.open}
                autoHideDuration={6000}
                onClose={() => setCertSnackbar({ ...certSnackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setCertSnackbar({ ...certSnackbar, open: false })}
                    severity={certSnackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {certSnackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Quiz;
